import React, { useState, useEffect, useRef, useContext, useCallback, useMemo, useReducer } from "react";
import Form from "react-bootstrap/Form";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import InputGroup from "react-bootstrap/InputGroup";
import { useAPIUpload, usePost } from "../API";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { InfoDict, QuestionProps } from "./Props";
import { languageContent, safeParse, SpacedSpinner, isPhone, useMarkdown, useInterval } from "../Util";
import { useParams } from "react-router-dom";
import InterfaceContext, { PublicConfigurationContext, SupportedLanguage } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { ListGroup } from "react-bootstrap";
import moment from "moment";
import { useToast, Icon, Alert, Button, Card, CardBody, CardHeader, Loading } from "@aidkitorg/component-library";
import { TurnableImage } from "../Components/TurnableImage";
import * as v0 from "@aidkitorg/types/lib/survey";
import { getURLsFromCSV } from '@aidkitorg/roboscreener/lib/util/urls_from_csv'
import { interfaceNumber } from "@aidkitorg/types/lib/survey";
import Camera from "../Components/Camera";
import slugid from 'slugid';
import { v4, v5 } from "uuid";
import { toMarkdownWithReplacements, useModularMarkdown } from "../Hooks/ModularMarkdown";
import * as Sentry from "@sentry/react";
import bsCustomFileInput from "bs-custom-file-input";
import { STRINGS } from "@aidkitorg/i18n/lib";
import { useAICoach } from "../AICoach/AICoachContext";
import { ApplicationStateContext } from "../Contexts/ApplicationStateContext";
import { ChatConversationContext } from "../Contexts/ChatContext";
import { RichText, Text } from "@aidkitorg/types/lib/survey";
import { useIsMobile } from "@aidkitorg/component-library";
import { IconName } from "@aidkitorg/component-library/src/Icon";
import { flattenAnalyses } from "@aidkitorg/types/lib/util";
import { AsyncMediaContext } from "../Contexts/AsyncMediaContext";

export type VerificationFieldContentType = {
  errors?: string[];
  warnings?: string[];
  acceptabilityStatus?: "acceptable" | "unacceptable" | "warning" | null;
  acceptabilityMessage?: string;
};

type WarningMessage = { errorCode: string } | { languageKey: string };

type AttachmentMetadata = {
  cleanedPath?: string,
  originalPath_DoNotOpen?: string,
  fileMetadata?: any,
  hash?: string,
  extractedTargetFields?: Record<string, string>
};

type AttachmentJob = {
  jobId: string,
  field?: string
}

export type AttachmentType = {
  metadata?: AttachmentMetadata,
  verifications?: VerificationFieldContentType,
  analyses?: { [targetField: string]: string },
  jobs?: { [targetField: string]: AttachmentJob }
};

type AttachmentState = {
  knownHashToUrlMap: { [hash: string]: string },
  attachments: {
    [url: string]: AttachmentType
  }
};

type AttachmentEvent = {
  type: 'remove',
  url: string
} | {
  type: 'upsert',
  url: string,
  verifications?: VerificationFieldContentType,
  analyses?: Record<string, string>,
  metadata?: AttachmentMetadata,
  jobs?: { [targetField: string]: AttachmentJob }
}

export const createPersistedVerificationKey = (targetField: string) => targetField + "_verification_results";

/**
 * @name Attachment
 * @description The Common way to display and manage a URL image
 */
export function Attachment(props: {
  url: string,
  key: string,
  removeURL?: (url: string) => void,
  info?: InfoDict,
  targetField?: string,
  style?: React.CSSProperties,
  turnable?: boolean,
  Viewer?: 'applicant' | 'screener',
  // Video options are at top level so you can do <Attachment playsInline muted /> for videos
  playsInline?: boolean,
  autoPlay?: boolean,
  muted?: boolean,
  showFullUrls?: boolean,
  renderPublicImages?: boolean,
}) {
  const L = useLocalizedStrings();
  const [signedURL, setSignedURL] = useState('');
  const getSignedURL = usePost('/document/view');
  
  const url = props.url;
  const urllower = url.toLowerCase();
  let urlToFetch = url;
  if ((urllower.endsWith("heic")) && props.info && props.info[props.targetField + "_conversion"]) {
    urlToFetch = props.info[props.targetField + "_conversion"]!;
  }

  const createSignedUrl = useCallback(async () => {
    if (!urlToFetch) return;

    if (!urlToFetch.includes('aidkit-documents')) {
      setSignedURL(urlToFetch);
      return;
    }

    try {
      const paths = await getSignedURL({
        paths: [urlToFetch],
      });
      if (paths.paths[urlToFetch]) {
        setSignedURL(paths.paths[urlToFetch]);
      }
    } catch (error) {
      console.error('Failed to fetch signed URL:', error);
    }
  }, [urlToFetch]);
  
  // The signed urls expire in 60 seconds, so refresh the url ahead of that expiry
  useInterval(createSignedUrl, 50_000, { fireImmediately: true, runInBackground: true });
  
  const createFullScreenURL = () => {
    const params = new URLSearchParams({
      src: signedURL,
      Viewer: props.Viewer || 'applicant'
    });
    return `/img?${params.toString()}`;
  };

  const showImages = (url: string, urlToRemove: string) => {
    const filename = url.split('/').pop()?.replace(/\.[^/.]+$/, '');
    return (
      <li style={props.style} className="mb-2">
        <Card expandable collapsedHeight={400}>
          <CardHeader
            title={<span className="text-sm font-mono">{props.showFullUrls ? url : filename}</span>}
            actionButton={props.removeURL ? <Button variant="danger" onClick={(e) => props.removeURL?.(urlToRemove)}>{L.questions.attachment.remove}</Button> : undefined}
          />
          <CardBody>
            <div className="overflow-auto my-2">
              {signedURL && url?.match(/\.mp4$|\.ogg$|\.webm$/)?.[0] ?
                <video className="w-full"
                  crossOrigin="anonymous"
                  onCanPlay={(e) => {
                    if (props.autoPlay) e.currentTarget.play();
                  }}
                  controls playsInline={props.playsInline} autoPlay={props.autoPlay} muted={props.muted}
                  src={signedURL}
                  preload='auto'
                />
                : signedURL && url?.match(/\.mp3$/)?.[0] ?
                  <audio className="w-full" controls preload='auto'>
                    <source src={signedURL} type="audio/mpeg" />
                  </audio>
                  : <a className='hover:no-underline' href={createFullScreenURL()} target="_blank"
                    rel="noopener noreferrer">

                    {signedURL && (
                      props.turnable || props.Viewer === 'screener' ?
                        <TurnableImage
                          Viewer={props.Viewer || 'applicant'}
                          alt="document"
                          src={signedURL}
                          fullScreenUrl={props.renderPublicImages ? signedURL : `${window.location.origin}${createFullScreenURL()}`}
                        /> : <img alt="document" style={{ maxWidth: props.style?.maxWidth || '200px' }} src={signedURL} />)
                    }
                  </a>}
            </div>
          </CardBody>
        </Card>
      </li>
    );
  }
  if (
    urllower.endsWith("png") ||
    urllower.endsWith("jpeg") ||
    urllower.endsWith("jpg") ||
    urllower.match(/mp4$|mp3$|ogg$|webm$/i)?.[0] ||
    url.split("/").slice(-1)[0].indexOf(".") === -1 ||
    props.renderPublicImages
  ) {
    return showImages(url, url);
  }
  
  if ((urllower.endsWith("heic")) && props.info && props.info[props.targetField + "_conversion"]) {
    return showImages(props.info[props.targetField + "_conversion"]!, url);
  }  

  return (
    <li>
      <a href={url}
        onClick={async (e) => {
          e.preventDefault();
          const paths = await getSignedURL({
            paths: [url],
          });
          window.open(paths.paths[url], "_blank");
        }}
        rel="noopener noreferrer" target="_blank">
        {props.showFullUrls ? url : url.split("/").slice(-1)[0]}
      </a>{" "}
      {props.removeURL && (
        <Button variant="secondary" onClick={(e) => props.removeURL!(url)}>
          {L.questions.attachment.remove}
        </Button>
      )}
    </li>
  );
}

export function createImageWindow(event: any) {

  event.preventDefault();

  const newWindow = window.open('_blank');

  let containerElement = null;
  let callback = (imageUrl: string) => {
    if (newWindow) {
      containerElement = newWindow.document.createElement('div');
      newWindow.document.body.appendChild(containerElement);

      newWindow.document.write('<title>AidKit Attachment</title>');

      newWindow.document.title = "AidKit Attachment";
      newWindow.document.write('<img style="max-width: 100%"; alt="document" src="' + imageUrl + '" />');
    }
  }

  return { newWindow, callback };
}

/**
 * This returns a string of markdown if the alert should be shown. Otherwise it returns null
 */
export function getInlineAlertContent(params: Parameters<typeof toMarkdownWithReplacements>[0]) { 
  return toMarkdownWithReplacements(params).trim() || null;  
}

export function InlineAlert({
  inlineAlert,
  uid,
  info
}: {
  inlineAlert: NonNullable<NonNullable<v0.Attachment["assistance"]>["inlineAlerts"]>[number],
  uid?: string,
  info: InfoDict
}) {
  const context = useContext(InterfaceContext);
  const alertContent = getInlineAlertContent({
    lang: context.lang,
    content: inlineAlert.content,
    info,
    uid
  });
  const markdown = useMarkdown(alertContent ?? "");

  if (!alertContent) {
    return null;
  }

  return <Alert
    iconName={inlineAlert.iconName as IconName}
    className="[&_h4]:text-sm [&_h4]:font-medium"
    variant={inlineAlert.variant}
    title={inlineAlert.title?.[context.lang as keyof typeof inlineAlert.title] || inlineAlert.title?.en || ''}>
    {markdown}
  </Alert>  
}

export function AssistanceAnalysisContent({ 
  analyses,
  jobs,
  assistance,
  uid,
  info
}: { 
  analyses: AttachmentType["analyses"],
  jobs: AttachmentType["jobs"],
  assistance: v0.Attachment["assistance"],
  uid?: string,
  info: InfoDict
}) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();

  const pendingJobs = Object.entries(jobs ?? {})
    .filter(([field, { jobId }]) => jobId && !analyses?.[field]);
  
  const allInfo = {
    ...info,
    ...analyses
  };
        
  let alerts = (assistance?.inlineAlerts ?? []).map((inlineAlert, i) => <InlineAlert 
    key={`attachmentInlineAlert:${i}`}
    inlineAlert={inlineAlert}
    info={allInfo}
    uid={uid} />
  );
  
  if (!alerts.length && !pendingJobs.length && assistance?.successMessage) {
    const successContent = (() => {
      const message = assistance.successMessage![context.lang as keyof typeof assistance.successMessage] || assistance.successMessage!.en;

      try {
        return toMarkdownWithReplacements({
          lang: context.lang,
          content: message,
          info: allInfo,
          uid
        });
      } catch (error) {
        console.log('Success message conditional evaluation failed, using plain message:', error instanceof Error ? error.message : String(error));
        return message;
      }
    })();
    
    // only show a success alert if successContent is specified on the assistance
    if (successContent.trim())  {
      const successMarkdown = useMarkdown(successContent);
      alerts = [<ul className="space-y-2">
        <Alert
          iconName="CheckCircleIcon"
          className="border border-green-500"
          variant="success">
          <div className="font-semibold">{L.ai_coach.document_signals_success_title}</div>
          {successMarkdown}
        </Alert>
      </ul>];      
    }
  }
  
  if (!pendingJobs.length && !alerts.length) {
    return null;
  }

  return <div className="flex flex-col gap-2">
    {assistance?.indicateAnalysisRunning ? pendingJobs.map(([field]) => <Loading key={field} />) : null}
    {alerts.map((alert, i) => <React.Fragment key={alert?.key}>{alert}</React.Fragment>)}
  </div>;
};

function FromComms(props: {
  uid?: string,
  attachments: Record<string, boolean>,
  setSelected: React.Dispatch<React.SetStateAction<string>>,
  setSelectedMetadata?: React.Dispatch<React.SetStateAction<string>>,
  setUrlMetadataLookup?: (event: AttachmentEvent) => void,
}) {
  const L = useLocalizedStrings();

  const { setSelected, setSelectedMetadata, setUrlMetadataLookup } = props
  const currentAttachments = props.attachments;

  const getMessages = usePost("/v2/applicant/get_messages");
  const [messages, setMessages] = useState({ data: [], unhandled_count: 0 } as Awaited<ReturnType<typeof getMessages>>);

  useEffect(() => {
    (async () => {
      if (!props.uid) return;
      const messageData = await getMessages({
        channel: 'default',
        applicant: props.uid
      });
      if (messageData) {
        setMessages(messageData);
      }

    })();
  }, [props.uid, getMessages]);

  if (!props.uid || !messages || !messages.data) return null;

  return (
    <ListGroup>
      {messages.data.map((m: any, i: number) => {
        const metadata = safeParse(m.metadata);
        if (!metadata?.attachments) return '';
        return metadata.attachments.map((a: string, i2: number) => {
          const alreadyExists = currentAttachments[a] === true;
          return (
            <ListGroup.Item key={`medialist-${i}-${i2}`} className="alternate-color">
              <h6>
                {m.source}, {moment(m.created_at).calendar()}
              </h6>
              <div className="d-flex justify-content-center">
                <Attachment key={`attachment-${a}`} style={{ maxHeight: '350px' }} url={a} />
              </div>
              <br />
              <div className="d-flex justify-content-end">
                {(alreadyExists && (
                  <button className="bg-gray-400 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded" disabled>{L.questions.attachment.already_added}</button>
                )) ||
                  <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onClick={() => {
                    setSelected(a)
                    setSelectedMetadata && setSelectedMetadata(metadata.attachmentMetadata);
                    // incoming message metadata gets stored in a lookup table on the metadata object. look for them and assign if available for the files url
                    setUrlMetadataLookup?.({
                      type: 'upsert',
                      url: a,
                      metadata: metadata.attachmentMetadata
                    })
                  }}>{L.questions.attachment.add_to_survey}</button>
                }
              </div>
            </ListGroup.Item>
          )
        })
      })}
    </ListGroup>
  );
}

function useTextMessage(expectResponse: boolean, language?: string): any {

  let message = STRINGS[(language && language in STRINGS ? language : 'en') as keyof typeof STRINGS].questions.attachment.reply_with_attachment;

  const [response, setResponse] = useState({});
  const [sent, setSent] = useState(false);

  const [waterline, setWaterline] = useState("");
  const refWaterline = useRef(waterline);
  useEffect(() => {
    refWaterline.current = waterline;
  }, [waterline]);

  const [sentTo, setSentTo] = useState("");
  const refSentTo = useRef(sentTo);
  useEffect(() => {
    refSentTo.current = sentTo;
  }, [sentTo]);

  const sendMessage = usePost("/messages/request_attachment");
  const expectMessage = usePost("/messages/check");

  useEffect(() => {
    if (!expectResponse) return;

    const interval = setInterval(async () => {
      if (refWaterline.current) {
        const data = await expectMessage({
          since: refWaterline.current,
          phone: refSentTo.current,
          channel: 'default'
        });
        setResponse(data || {});
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [expectResponse]);

  const send = useCallback(async (phone: string) => {
    setSentTo(phone);
    const data = await sendMessage({
      phone: phone,
      is_attachment_request: true,
      message,
      channel: 'default'
    });
    if (data && 'created_at' in data && data.created_at) {
      setWaterline(data.created_at);
      setSent(true);
    }
  }, [language]);

  function finish() {
    setWaterline("");
    setSent(false);
  }

  if (expectResponse) {
    return [send, sent, response, finish];
  }

  return [send, sent];
}

function CameraAttachment({ expectMore, enableCamera, onStateChange, doUpload, enabled }: {
  expectMore?: boolean,
  enableCamera?: boolean,
  enabled?: boolean,
  onStateChange(state: 'enabled' | 'disabled'): void,
  doUpload(files?: File[] | undefined): Promise<void>
}) {
  return <Camera
    hidden={!enableCamera || isPhone()}
    onStateChange={onStateChange}
    enabled={enabled}
    onSnapshot={async (image) => {

      function dataURLtoBlob(dataurl: string) {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1];
        const bstr = atob(arr[1]);
        let n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
      }

      const name = slugid.encode(v5(image, v5.URL))

      await doUpload([new File([dataURLtoBlob(image)], `${name}.png`, { type: 'image/png' })]);

    }} />

}

async function calculateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

// audio/* accepts all audio types but doesn't appear to work on iOS, so as a fallback for iOS also list all audio types manually.
// List from https://www.iana.org/assignments/media-types/media-types.xhtml#audio
const AUDIO_ACCEPT_TYPES = 'audio/*, audio/1d-interleaved-parityfec, audio/32kadpcm, audio/3gpp, audio/3gpp2, audio/aac, audio/ac3, audio/AMR, audio/AMR-WB, audio/amr-wb+, audio/aptx, audio/asc, audio/ATRAC-ADVANCED-LOSSLESS, audio/ATRAC-X, audio/ATRAC3, audio/basic, audio/BV16, audio/BV32, audio/clearmode, audio/CN, audio/DAT12, audio/dls, audio/dsr-es201108, audio/dsr-es202050, audio/dsr-es202211, audio/dsr-es202212, audio/DV, audio/DVI4, audio/eac3, audio/encaprtp, audio/EVRC, audio/EVRC-QCP, audio/EVRC0, audio/EVRC1, audio/EVRCB, audio/EVRCB0, audio/EVRCB1, audio/EVRCNW, audio/EVRCNW0, audio/EVRCNW1, audio/EVRCWB, audio/EVRCWB0, audio/EVRCWB1, audio/EVS, audio/example, audio/flac, audio/flexfec, audio/fwdred, audio/G711-0, audio/G719, audio/G7221, audio/G722, audio/G723, audio/G726-16, audio/G726-24, audio/G726-32, audio/G726-40, audio/G728, audio/G729, audio/G7291, audio/G729D, audio/G729E, audio/GSM, audio/GSM-EFR, audio/GSM-HR-08, audio/iLBC, audio/ip-mr_v2.5, audio/L8, audio/L16, audio/L20, audio/L24, audio/LPC, audio/matroska, audio/MELP, audio/MELP600, audio/MELP1200, audio/MELP2400, audio/mhas, audio/midi-clip, audio/mobile-xmf, audio/MPA, audio/mp4, audio/MP4A-LATM, audio/mpa-robust, audio/mpeg, audio/mpeg4-generic, audio/ogg, audio/opus, audio/parityfec, audio/PCMA, audio/PCMA-WB, audio/PCMU, audio/PCMU-WB, audio/prs.sid, audio/QCELP, audio/raptorfec, audio/RED, audio/rtp-enc-aescm128, audio/rtploopback, audio/rtp-midi, audio/rtx, audio/scip, audio/SMV, audio/SMV0, audio/SMV-QCP, audio/sofa, audio/sp-midi, audio/speex, audio/t140c, audio/t38, audio/telephone-event, audio/TETRA_ACELP, audio/TETRA_ACELP_BB, audio/tone, audio/TSVCIS, audio/UEMCLIP, audio/ulpfec, audio/usac, audio/VDVI, audio/VMR-WB, audio/vnd.3gpp.iufp, audio/vnd.4SB, audio/vnd.audiokoz, audio/vnd.blockfact.facta, audio/vnd.CELP, audio/vnd.cisco.nse, audio/vnd.cmles.radio-events, audio/vnd.cns.anp1, audio/vnd.cns.inf1, audio/vnd.dece.audio, audio/vnd.digital-winds, audio/vnd.dlna.adts, audio/vnd.dolby.heaac.1, audio/vnd.dolby.heaac.2, audio/vnd.dolby.mlp, audio/vnd.dolby.mps, audio/vnd.dolby.pl2, audio/vnd.dolby.pl2x, audio/vnd.dolby.pl2z, audio/vnd.dolby.pulse.1, audio/vnd.dra, audio/vnd.dts, audio/vnd.dts.hd, audio/vnd.dts.uhd, audio/vnd.dvb.file, audio/vnd.everad.plj, audio/vnd.hns.audio, audio/vnd.lucent.voice, audio/vnd.ms-playready.media.pya, audio/vnd.nokia.mobile-xmf, audio/vnd.nortel.vbk, audio/vnd.nuera.ecelp4800, audio/vnd.nuera.ecelp7470, audio/vnd.nuera.ecelp9600, audio/vnd.octel.sbc, audio/vnd.presonus.multitrack, audio/vnd.qcelp, audio/vnd.rhetorex.32kadpcm, audio/vnd.rip, audio/vnd.sealedmedia.softseal.mpeg, audio/vnd.vmx.cvsd, audio/vorbis, audio/vorbis-config';

export function AttachmentQuestion(props: QuestionProps) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const { toast, dismiss } = useToast();

  const { subsurvey } = useParams() as Record<string, string>;

  const [send, sent, response, finish] = useTextMessage(true, props.info["language"]);
  const [phone, setPhone] = useState((props['Additional Options'] || []).indexOf('Default to Screener Phone') !== -1 ?
    props.screenerMetadata['Phone'].replace(/^([0-9]?[0-9]?[0-9]?[0-9]?[0-9]?[0-9]?[0-9]?[0-9]?[0-9]?[0-9]?)(.*)$/g, "$1") :
    props.info["phone_number"]);

  const field = props["Target Field"];

  const loadAttachments = useCallback(() => {
    return getURLsFromCSV(props.info[props["Target Field"]!] || "")
      .reduce((acc, url: string) => {
        acc[url] = true;
        return acc;
      }, {} as Record<string, boolean>);
  }, [props.info, props["Target Field"]]);

  const [attachments, setAttachments] = useState(loadAttachments());
  const attachmentsRef = useRef(attachments);
  const [selectedFiles, setSelectedFiles] = useState([] as File[]);
  const AICoach = useAICoach();
  const chat = useContext(ChatConversationContext);
  const { applicationStateChanged } = useContext(ApplicationStateContext);
  const realtimeMedia = useContext(AsyncMediaContext);

  const [pendingAdds, setPendingAdds] = useState([] as any[]);
  const [pendingDeletes, setPendingDeletes] = useState([] as any[]);
  const [progress, setProgress] = useState(null as null | string);
  const [processingUpload, setProcessingUpload] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [sendingSMSLink, setSendingSMSLink] = useState(false);
  const [sendingEmailLink, setSendingEmailLink] = useState(false);

  const sendLinkToSubsurvey = usePost('/applicant/send_subsurvey_link');

  const targetField = props["Target Field"]!;
  const persistedVerificationKey = createPersistedVerificationKey(targetField);
  const metadataKey = `${targetField}_metadata`;
  const extractedFieldsKey = `${targetField}_extracted_fields`;

  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);

  const { limitOne, realtimeVerifications, allowSendingLinkToUpload, enableCamera, humanReadableName, dragAndDrop, assistance } = safeParse(props.Metadata || '{}') as {
    limitOne?: boolean,
    realtimeVerifications?: v0.VerificationRequirements[],
    allowSendingLinkToUpload?: boolean,
    enableCamera: boolean,
    humanReadableName: RichText,
    dragAndDrop?: boolean,
    assistance?: v0.Attachment['assistance'],
  };
  
  const analyses = useMemo(
    () => flattenAnalyses(assistance?.analyses ?? []),
    [assistance?.analyses?.length]
  );
  
  const asyncAnalysisFields = useMemo(() => analyses.filter(a => a.processAsync).map(a => a.targetField), [analyses]);
  
  const initialState: AttachmentState = useMemo(() => {
    const verifications: { [url: string]: VerificationFieldContentType } = safeParse(props.info[persistedVerificationKey], {});
    const metadata: { [url: string]: AttachmentMetadata } = safeParse(props.info[metadataKey], {});
    const attachments: AttachmentState['attachments'] = {};
    const knownHashToUrlMap: AttachmentState['knownHashToUrlMap'] = {};

    const analysesTargetFields = analyses
      .map(a => a.targetField)
      .reduce((urlMap, targetField) => {

        const analysisByUrl: Record<string, string> = safeParse(props.info[targetField], {});

        for (const [url, value] of Object.entries(analysisByUrl)) {
          urlMap[url] = {
            ...(urlMap[url] || {}),
            [targetField]: value
          };
        }

        return urlMap;
      }, {} as { [url: string]: Record<string, string> });

    for (const url of Object.keys(verifications).concat(Object.keys(metadata)).concat(Object.keys(analysesTargetFields))) {
      attachments[url] = {
        metadata: metadata[url],
        verifications: verifications[url],
        analyses: analysesTargetFields[url]
      }

      const hashKey = metadata[url]?.hash;
      if (hashKey) {
        knownHashToUrlMap[hashKey] = url;
      }
    }

    return {
      attachments,
      knownHashToUrlMap
    };
  }, []);

  const [attachmentState, attachmentDispatch] = useReducer((state: AttachmentState, event: AttachmentEvent) => {
    switch (event.type) {
      case 'upsert':

        const existing = state.attachments[event.url];

        const hashKey = existing?.metadata?.hash ?? event.metadata?.hash;

        return {
          knownHashToUrlMap: {
            ...state.knownHashToUrlMap,
            ...(hashKey ? { [hashKey]: event.url } : {})
          },
          attachments: {
            ...state.attachments,
            [event.url]: {
              metadata: {
                ...(existing?.metadata ?? {}),
                ...(event.metadata ?? {})
              },
              verifications: {
                ...(existing?.verifications ?? {}),
                ...(event.verifications ?? {})
              },
              analyses: {
                ...(existing?.analyses ?? {}),
                ...(event.analyses ?? {})
              },
              jobs: {
                ...(existing?.jobs ?? {}),
                ...(event.jobs ?? {})
              }
            }
          }
        };

      case 'remove':

        const original = state.attachments[event.url];
        
        if (original) {
          delete state.attachments[event.url];
        }

        const originalHashKey = original?.metadata?.hash;

        return {
          ...state,
          knownHashToUrlMap: Object.fromEntries(
            Object.entries(state.knownHashToUrlMap)
              .filter(([hashKey, url]) => url !== event.url && (!originalHashKey || originalHashKey !== hashKey))
          )
        }

      default:
        break;
    }

    return state;
  }, initialState);

  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  const fieldName = humanReadableName?.[context.lang] ?? targetField;

  // Get all media messages for this applicant
  const [selectedMediaFromMessage, setSelectedMediaFromMessage] = useState('');
  const [selectedMetadataFromMessage, setSelectedMetadataFromMessage] = useState('');
  const [pendingAddsFromComms, setPendingAddsFromComms] = useState([] as any[]);

  const publicConfig = useContext(PublicConfigurationContext);
  const agentName = publicConfig?.capabilities?.aiCoach?.appearance?.name || 'Opal';

  const dragAndDropOnGlobally = publicConfig?.experimental?.enableDragAndDropGlobally;

  const isDragAndDropEnabled = dragAndDropOnGlobally || dragAndDrop;

  useEffect(() => {
    bsCustomFileInput.init();
  }, []);

  useEffect(() => {

    const urlToVerifications =
      Object.entries(attachmentState.attachments)
        .filter(([url]) => typeof attachmentState.attachments[url] === 'object')
        .map(([url]) => ({ url, ...attachmentState.attachments[url] }))

    const states = urlToVerifications.map(({ verifications: verification }) =>
      !verification?.acceptabilityStatus ?
        'unentered' :
        verification?.acceptabilityStatus === 'unacceptable' ?
          'invalid' :
          'valid'
    );

    applicationStateChanged({
      type: 'change',
      questionId: props.Question,
      update: {
        validity: {
          state: !states.length ? 'unentered' : states.some(s => s === 'invalid') ? 'invalid' : 'valid',
          message: `The "${fieldName}" document upload field has the following acceptability states:
            ${JSON.stringify(urlToVerifications.map(({ url, verifications: verification }) => ({ url, ...verification })))}
          `
        }
      }
    });
  }, [attachmentState.attachments]);

  const replacer = useCallback((match: string) => {
    const variable = match.slice(1).replace(/\\_/g, '_');
    
    if (variable === "uid") return props.uid;
    
    if (props.info[variable] !== undefined && props.info[variable] !== null) {
      return props.info[variable];
    }
    return match;
  }, [props.uid, props.info]);  
  
  const marked = useModularMarkdown({
    info: props.info,
    content: props[languageContent(context.lang)] || '',
    replacer: (str) => str.replace(/\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g, replacer)
  });

  useEffect(() => {
    if (attachmentsLoaded) return;
    setAttachmentsLoaded(true);
    setAttachments(loadAttachments());
  }, [field, targetField, attachmentsLoaded, props.info]);

  const uploadURL = usePost("/document/upload_url");
  const checkLegibility = usePost("/document/check_legibility");
  const documentAnalysis = usePost('/ai/document/analysis');

  const upload = useAPIUpload("/upload", (progress: number) => {
    setProgress(`${Math.round(progress * 100)}%`);
  });

  const cleanUpload = usePost("/document/clean_upload", { suppressErrorDisplay: true });

  useEffect(() => {
    if (response && response.attachments) {
      setPendingAdds((prevState) => {
        return Array.prototype.concat.apply(prevState, response.attachments);
      });
    }
  }, [response]);
  
  const info = props.info;
  const originalSetInfoKey = props.setInfoKey;
  const setInfoKey = (key: string, value: any, save: boolean, validate: boolean) => {
    return originalSetInfoKey(key, value, save, validate);
  };
  const target = props["Target Field"]!;
  const directUploadOnly = (props["Additional Options"] || []).includes("Direct Upload Only");

  // Persist verification results whenever they change
  const persistVerificationResults = useCallback(() => {
    if (realtimeVerifications?.length) {
      const verifications = Object.entries(attachmentState.attachments)
        .reduce((acc, [url, { verifications }]) => ({ ...acc, [url]: verifications }), {});

      setInfoKey(persistedVerificationKey, JSON.stringify(verifications), true, false);
    }
  }, [realtimeVerifications, attachmentState]);
  
  const persistAnalysisJobs = useCallback(() => {
    if (assistance && !assistance.disabled && attachmentState.attachments) {      
      const jobsByTargetField = Object.entries(attachmentState.attachments ?? {})
        .reduce((acc, [url, { jobs }]) => { 
          for (const [field, job] of Object.entries(jobs ?? {})) {
            acc[field] ??= {};
            acc[field][url] = job;
          }
          return acc;
        }, {} as { [targetField: string]: { [url: string]: AttachmentJob }});
      
      for (const [targetField, urlJobMap] of Object.entries(jobsByTargetField)) {
        setInfoKey(`${targetField}_jobs`, JSON.stringify(urlJobMap), true, false);
      }
    }
  }, [assistance, attachmentState]);  

  // Persist verification results and analysis jobs when they change
  useEffect(() => {
    persistVerificationResults();
    persistAnalysisJobs();
  }, [attachmentState]);

  // Overwrite attachments if selected media from a message
  useEffect(() => {
    if (selectedMediaFromMessage) {
      setPendingAddsFromComms((prevState) => {
        return Array.prototype.concat.apply(prevState, [selectedMediaFromMessage]);
      });

      setSelectedMediaFromMessage('');
    }
  }, [selectedMediaFromMessage]);

  // If our attachments update
  useEffect(() => {
    // Check if we have things to save, and do so.
    if (pendingAddsFromComms.length || pendingAdds.length || pendingDeletes.length) {    
      const nextAttachments = Object.assign({}, attachments);
      for (const add of pendingAddsFromComms) {
        nextAttachments[add] = true;
      }

      for (const add of pendingAdds) {
        if (nextAttachments[add] === undefined) {
          nextAttachments[add] = true;
        }
      }
      for (const del of pendingDeletes) {
        nextAttachments[del] = false;
        attachmentDispatch({
          type: 'remove',
          url: del
        });
        // receive any target fields from realtimeMedia that need to be changed
        const updates = realtimeMedia.removeImage(del);
        for (const [k, v] of Object.entries(updates)) {
          setInfoKey(k, v, true, false);
        }
      }
      const list = Object.keys(nextAttachments)
        .filter((url) => nextAttachments[url])
        .join(",");

      // Get metadata for any current urls, preserving order and association with URLs
      const currentUrls = Object.keys(nextAttachments).filter(url => nextAttachments[url]);
      const metadataMap = currentUrls
        .reduce((acc, url) => {
          const metadata = attachmentState.attachments[url]?.metadata;

          if (metadata) {
            acc[url] = metadata;
          }

          return acc;
        }, {} as { [url: string]: AttachmentMetadata });

      // Only update if the list has actually changed
      if (list !== info[target]) {

        setInfoKey(target, list, true, false);
        setInfoKey(metadataKey, JSON.stringify(metadataMap), true, false);

        if (realtimeVerifications?.length) {
          // Store extracted fields separately for easier screener access
          const extractedFieldsMap = currentUrls.reduce((acc, url) => {
            const extracted = attachmentState.attachments[url]?.metadata?.extractedTargetFields;
            if (extracted) {
              acc[url] = extracted;
            }
            return acc;
          }, {} as { [url: string]: Record<string, string> });

          setInfoKey(extractedFieldsKey, JSON.stringify(extractedFieldsMap), true, false);
        }

        if (assistance && !assistance.disabled) {
          const analysisMap = currentUrls.reduce((acc, url) => {
            const { analyses } = attachmentState.attachments[url] ?? {};
            if (analyses) {
              for (const [targetField, value] of Object.entries(analyses)) {
                acc[targetField] = {
                  ...(acc[targetField] || {}),
                  [url]: value
                };
              }
            }
            return acc;
          }, {} as { [targetField: string]: Record<string, string> });

          for (const [targetField, value] of Object.entries(analysisMap)) {
            setInfoKey(targetField, limitOne ? Object.values(value)[0] : JSON.stringify(value), true, false);
          }
        }

        setAttachments(nextAttachments);
      }
      setPendingAddsFromComms([]);
      setPendingAdds([]);
      setPendingDeletes([]);
      finish();
    }
  }, [setInfoKey, pendingAdds, pendingDeletes, pendingAddsFromComms, attachmentState]);

  // Create a ref of the attachments object to use in the useEffect below.
  // This allows validate against attachments without triggering the useEffect when `attachments` changes.
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    const nextAttachments = getURLsFromCSV(info[target] || "");
    let stale = false;
    for (const key in attachmentsRef.current) {
      if (attachmentsRef.current[key] === true && nextAttachments.indexOf(key) === -1) {
        stale = true;
      }
    }
    for (const key of nextAttachments) {
      if (attachmentsRef.current[key] !== true) {
        stale = true;
      }
    }
    if (stale === false) return;

    setAttachments((prevState) => {
      const nextState: any = Object.assign({}, prevState);
      for (const key in nextState) {
        nextState[key] = false;
      }
      for (const key of nextAttachments) {
        nextState[key] = true;
      }
      return nextState;
    });
  }, [info[target]]);

  // when async field values change, populate the `assistance`
  useEffect(() => {
    const analysesByUrl = asyncAnalysisFields.reduce(
      (acc, field) => { 
        const urlMap: { [imageUrl: string]: string } = safeParse(info[field], {});
        for (const [imageUrl, value] of Object.entries(urlMap)) {
          acc[imageUrl] ??= {};
          acc[imageUrl][field] = value;
        }
        return acc;
      },
      {} as { [imageUrl: string]: { [analysisTargetField: string]: string } }
    )
    for (const [imageUrl, analyses] of Object.entries(analysesByUrl)) { 
      attachmentDispatch({
        type: "upsert",
        url: imageUrl,
        analyses
      });
    }
  }, [...asyncAnalysisFields.map(field => info[field])])
  
  const fileInput = useRef<HTMLFormElement>(null as any);
  const fileInputLabel = useRef<HTMLLabelElement>(null as any);
  const fileInputRef = useRef<HTMLInputElement>(null as any);
  const uploading = useRef(false);

  const doUpload = useCallback(async (files?: File[]) => {
    if (!files && selectedFiles) {
      files = selectedFiles;
    }
    if (!files || files?.length === 0) return;

    for (const file of files) {
      const fileHash = await calculateFileHash(file);

      if (attachmentState.knownHashToUrlMap[fileHash]) {
        toast({
          id: `duplicate_results_found_notif_${file.name}`,
          autoClose: true,
          duration: 5_000,
          description: `${L.questions.attachment.duplicate_attachment_found}: ${file.name}`,
          variant: 'error'
        });
        fileInput.current.reset();
        continue;
      }

      setProgress('0%');
      uploading.current = true;
      const url = await uploadURL({
        path: file.name,
        length: file.size,
        phone: props.info["phone_number"],
      });

      let result;
      const attachmentUpdates: Extract<AttachmentEvent, { type: 'upsert' }> = {
        type: 'upsert',
        url: url.savedPath!,
        metadata: {
          hash: fileHash
        }
      };

      try {
        result = await upload(files as unknown as any[] || selectedFiles, {}, url.uploadURL, "PUT");
        setProcessingUpload(true);

        if (typeof result == 'string' && url.savedPath) {
          setProgress(null);
          const cleanResult = await cleanUpload({
            savedPath: url.savedPath,
          });

          // Use cleaned url from cleanUpload instead of initial savedPath
          if (cleanResult.savedPath) {
            url.savedPath = cleanResult.savedPath;

            attachmentUpdates.url = cleanResult.savedPath;
            attachmentUpdates.metadata = {
              ...attachmentUpdates.metadata,
              ...cleanResult.metadata
            };

            if (cleanResult.infoForUser === 'pdf_sliced_to_20_pages') {
              alert(L.questions.attachment.pdf_sliced_to_20_pages);
            }
          } else if (cleanResult.validation_error) {
            if (cleanResult.validation_error === "password_protected_pdf") {
              alert(L.questions.attachment.can_not_process_password_protected_files);
            } else if (cleanResult.validation_error === "empty_file") {
              alert(L.questions.attachment.can_not_process_empty_file);
            }
            fileInput.current.reset();
            fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
            setProcessingUpload(false);
            setProgress(null);
            return;
          } else {
            const fileTooLarge = (cleanResult as any).error === 'File too large';
            const devDetonationError = (cleanResult as any).error === 'Could not reach detonation in dev. Did you run `./dev detonation`?';
            const error = fileTooLarge
              ? L.questions.attachment.file_too_big
              : devDetonationError
                ? (cleanResult as any).error
                : L.questions.attachment.could_not_process_file_please_try_a_different_file_or_type;
            alert(error);
            throw new Error("Error processing upload");
          }
        }
      } catch (e) {
        fileInput.current.reset();
        fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
        setProcessingUpload(false);
        setProgress(null);
        toast({
          description: L.questions.attachment.error_uploading_image,
          variant: 'error'
        });
        Sentry.captureException(e, { extra: { uid: props.uid, url: url.savedPath } });
        continue;
      }
      uploading.current = false;
      setProgress(null);
      if (typeof result === 'string' && url.savedPath) {
        if (fileInput.current) {
          fileInput.current.reset();
          if (fileInputLabel.current) fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
        }

        if (assistance && !assistance.disabled) {
          try {
            const analysisResult = await documentAnalysis({ targetField, path: url.savedPath, info: props.info });
            if (!("error" in analysisResult)) {
              // results get dispatched to reducer
              attachmentUpdates.analyses = Object.fromEntries(analysisResult.results.map(({ field, value }) => [field, value]));              
              // group jobs by targetField for attachmentState
              attachmentUpdates.jobs = Object.fromEntries(
                analysisResult
                  .jobs.filter(job => job.field).map(job => [job.field, job])
              );
            }
          } catch (e) {
            console.error('failed to run document assistance. skipping.', e);
            attachmentUpdates.analyses = {};
          }
        }

        if (realtimeVerifications) {
          const passed = await checkLegibility({
            path: url.savedPath,
            targetField: targetField,
            info: props.info
          });

          let errs: string[] = [];
          for (const e of (passed?.errorMsgs || []) as Text[]) {
            errs.push(e[context.lang as keyof Text] || e.en || '');
          }

          const verificationUpdates: Partial<VerificationFieldContentType> = attachmentUpdates.verifications ?? {
            errors: errs
          };
          attachmentUpdates.verifications = verificationUpdates;

          let warnings: string[] = [];
          const SupportedFileTypes = ['png', 'jpeg', 'jpg', 'gif', 'webp'];

          for (const e of passed.warningMsgs as WarningMessage[]) {
            if ('errorCode' in e) {
              // AI verification warnings with error codes
              const { errorCode } = e;

              const configuredError = realtimeVerifications
                ?.flatMap(v => v.aiVerification?.errorMessages || [])
                ?.find(msg => msg.error === errorCode);

              const displayMessage = configuredError?.detail?.[context.lang]
                || configuredError?.detail?.en
                || errorCode;

              warnings.push(displayMessage);
            } else if ('languageKey' in e) {
              //  languageKey warnings (non-AI verifications)  
              const localizedMessage = (L as any)[e.languageKey];
              warnings.push(typeof localizedMessage === 'string' ? localizedMessage : e.languageKey);
            }
          }

          verificationUpdates.warnings = warnings;

          if (!passed.pass || warnings.length > 0) {
            verificationUpdates.acceptabilityStatus = 'unacceptable';
          } else {
            verificationUpdates.acceptabilityStatus = 'acceptable';
            verificationUpdates.acceptabilityMessage = L.ai_coach.document_verification_success;
          }

          if (!realtimeVerifications.some(v => v.aiVerificationPresent)) {
            toast({
              description: L.questions.attachment.document_verification_success,
              variant: 'success'
            });
          }

          // Only block the upload if there are actual errors (not just warnings)
          if (!passed.pass && errs.length > 0) {
            setProgress(null);
            return;
          }

          // Extract target fields from verification results
          if (passed.verificationResults && typeof passed.verificationResults === 'object') {
            attachmentUpdates.metadata = {
              ...(attachmentUpdates.metadata ?? {}),
              extractedTargetFields: passed.verificationResults?.extractedFields
            }
          }

          attachmentUpdates.verifications = {
            ...(attachmentUpdates.verifications ?? {}),
            errors: errs,
            warnings: warnings,
          }
        }
        // Note: If no verification is configured (neither existingResults nor realtimeVerifications),
        // we proceed normally without verification - this is the expected behavior
        setPendingAdds((prevState) => {
          return Array.prototype.concat.apply(prevState, [url.savedPath]);
        });
      } else {
        // No verification, just add the file
        setPendingAdds((prevState) => {
          return Array.prototype.concat.apply(prevState, [url.savedPath]);
        });
      }
      attachmentDispatch(attachmentUpdates);
      setProcessingUpload(false);
    }
  }, [selectedFiles, props.info, realtimeVerifications, publicConfig?.experimental, AICoach.enabled, attachmentState]);

  function removeURL(url: string) {
    setPendingDeletes((prevState) => {
      return Array.prototype.concat.apply(prevState, [url]);
    });
  }

  const renderedAttachments = <ul className="m-0">
    {Object.entries(attachments).filter(([, visible]) => visible).map(([url]) => {
      if (!attachments[url]) return null;

      const { verifications: verification, analyses, jobs } = attachmentState.attachments?.[url] ?? {};

      const {
        acceptabilityStatus,
        warnings: attachmentWarnings = [],
        errors: attachmentErrors = []
      } = verification ?? {};

      return <div key={url}>
        <Attachment info={props.info} targetField={props["Target Field"]}
          key={url}
          url={url} removeURL={removeURL} Viewer={props.Viewer} />

        { assistance && <AssistanceAnalysisContent
          analyses={analyses}
          jobs={jobs}
          assistance={assistance}
          info={props.info}
          uid={props.uid}
        /> }

        {!!verification && (
          <div className="my-2 space-y-2">
            {/* Verification warnings and errors */}
            {((attachmentErrors.length > 0 || attachmentWarnings.length > 0)) && (
              <Alert
                variant="warning"
              >
                <span className="font-bold">{L.questions.attachment.lets_fix_a_few_things}</span>
                <span>
                  {attachmentErrors.map((error, index) => (
                    <div key={`error-${index}`}>{error}</div>
                  ))}
                  {attachmentWarnings.map((warning, index) => {
                    const trimmedWarning = warning.trim();
                    const formattedWarning = trimmedWarning.endsWith('.') ? trimmedWarning : `${trimmedWarning}.`;
                    return <div key={`warning-${index}`}>{formattedWarning}</div>;
                  })}
                </span>

                {realtimeVerifications?.some(v => v.aiVerificationPresent) && attachmentWarnings.length > 0 && AICoach.enabled && (
                  <Button
                    icon={<Icon name="ChatBubbleLeftRightIcon" />}
                    variant="secondary"
                    onClick={() => {
                      // TODO, just open the chat directly, don't try and click a link to open the chat
                      const chatButton = document.querySelector(`[aria-label="Ask ${agentName}: Your AI Coach"]`);
                      if (chatButton && chatButton instanceof HTMLElement) {
                        chatButton.click();
                        const id = slugid.encode(v4());
                        chat.dispatch({
                          id,
                          role: 'system',
                          type: 'message',
                          content: [{
                            type: 'text',
                            content: `Instruct the user that they uploaded a document for the ${humanReadableName?.[context.lang] ?? targetField} field that resulted in the following verification warnings:\n${attachmentWarnings.map(warning => `* ${warning}`)}`,
                          }],
                          meta: {
                            createResponse: true
                          }
                        });
                      }
                    }}
                  >
                    <span>{L.questions.attachment.get_help_from_agent.replaceAll('$agent', agentName)}</span>
                  </Button>
                )}
              </Alert>
            )}

            {/* Success message when verification passes */}
            {(attachmentErrors.length === 0 && attachmentWarnings.length === 0 && acceptabilityStatus === 'acceptable' && Object.keys(attachments).some(k => attachments[k])) && (
              <Alert
                variant="success"
              >
                <span>
                  {realtimeVerifications?.some(v => v.aiVerificationPresent)
                    ? L.ai_coach.document_verification_success
                    : L.questions.attachment.document_verification_success}
                </span>
              </Alert>
            )}
          </div>
        )}
      </div>
    })}
  </ul>

  const sendLinkToGeneratedSuburvey = useCallback(async (method: 'sms' | 'email') => {
    const applicant = props.uid;
    if (!applicant) {
      return;
    }
    const result = await sendLinkToSubsurvey({
      channel: 'default',
      method,
      applicant,
      subsurvey: props['Target Field'] + '_automated_collection_form',
      message: L.questions.attachment.use_link_to_upload
    });
    if (result && (result as any).status) {
      toast({
        description: L.applicant.link_sent,
        variant: 'success'
      });
    } else {
      toast({
        description: L.applicant.comms.message_failed,
        variant: 'error'
      });
    }
  }, [props.uid]);

  const useNewInterface = useMemo(() => {
    const interfaceVersion = interfaceNumber(publicConfig?.interface?.version);
    return props.Viewer === 'applicant' || (interfaceVersion > 0);
  }, [props.Viewer, publicConfig?.interface?.version]);


  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const accept = `application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, image/*, image/heic, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.oasis.opendocument.spreadsheet${realtimeVerifications ? "" : `, ${AUDIO_ACCEPT_TYPES}, video/*`}`

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);

    if (files?.length) {
      const file = files[0];
      // check if file type is supported
      const supportedTypes = accept.split(',').map(f => f.trim())
      // Check if the file type is directly supported
      if (
        !supportedTypes.includes(file.type) &&
        !supportedTypes.includes(file.type.split('/')[0] + '/*')
      ) {
        return alert(L.questions.attachment.file_type_not_supported);
      }

      if (file.size > 10485760) {
        fileInput.current.reset();
        fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
        return alert(L.questions.attachment.file_too_big);
      }
      await doUpload(files);
    }
  };

  const uploadForm = useMemo<JSX.Element>(() => <form ref={fileInput} className={(useNewInterface ? "hidden" : "")}>
    <InputGroup>
      <div className="custom-file">
        <input
          ref={fileInputRef}
          multiple
          className="custom-file-input"
          id={"fileupload" + props["Target Field"]!}
          onChange={async (e) => {
            e.preventDefault();
            let files = e.target.files;

            if (files && files.length === 0) {
              fileInput.current.reset();
              fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
            } else {
              if (files && files[0].size > 10485760) {
                fileInput.current.reset();
                fileInputLabel.current.innerHTML = L.questions.attachment.choose_files;
                return alert(L.questions.attachment.file_too_big);
              }
              if (files) {
                if (useNewInterface) {
                  await doUpload(Array.from(files));
                } else {
                  setSelectedFiles(Array.from(files))
                }
              }
            }
          }}
          type="file"
          accept={accept}
        />
        <label
          ref={fileInputLabel}
          className={"custom-file-label"}
          htmlFor={"fileupload" + props["Target Field"]!}
        >
          {L.questions.attachment.choose_files}
        </label>
      </div>
      <InputGroup.Append>
        <Button type="button" style={{ 'zIndex': 10000 }} onClick={async (e) => { e.preventDefault(); await doUpload() }} variant="primary">
          {uploading.current && <SpacedSpinner />}<span>{L.questions.attachment.upload}</span>
        </Button>
      </InputGroup.Append>
    </InputGroup>
  </form>, [doUpload, useNewInterface]);

  const expectMore = useMemo(
    () => Object.keys(attachments || {}).filter(k => attachments[k]).length === 0 || (props['Additional Options'] || []).indexOf("Multiple") !== -1,
    [attachments, props['Additional Options']]
  );

  if (props["Additional Options"]?.includes('Hidden')) {
    return null;
  }

  if (useNewInterface) {
    return <div>
      {marked}
      {renderedAttachments}
      {uploadForm}
      {(limitOne ? expectMore : true) &&
        <div>
          {(progress || processingUpload)
            && (
              <Card>
                <CardBody>
                  <div className="p-4">
                    <Loading labels={[{
                      content: processingUpload
                        ? <span>{`${L.questions.attachment.analyzing}`}</span>
                        : <span>{`${L.questions.attachment.uploading} ${progress}`}</span>
                    }]}>
                    </Loading>
                  </div>
                </CardBody>
              </Card>
            )
          }
        </div>
      }

      {(!progress && !processingUpload) && (limitOne ? expectMore : true) && (
        <div
          {...(!isMobile && isDragAndDropEnabled ? {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
            className: `mt-4 space-x-2 flex flex-col items-center justify-center p-6 border ${isDragging ? 'border-blue-500 bg-blue-100' : 'border-gray-300 bg-gray-50'} rounded-md`
          } : {})
          }
        >
          <div className="mt-4 space-x-2 flex flex-row">
            <Button
              hidden={cameraActive}
              variant="secondary"
              icon={<Icon name="ArrowUpOnSquareIcon" />}
              onClick={
                async (e) => {
                  e.preventDefault();
                  fileInputLabel.current.click();
                }
              }
            >
              {expectMore ? L.questions.attachment.upload_document : L.questions.attachment.upload_additional}
            </Button>
            <CameraAttachment
              expectMore={expectMore}
              enableCamera={enableCamera}
              onStateChange={(s) => setCameraActive(s === 'enabled')}
              doUpload={doUpload}
            />
          </div>
          {(!isMobile && isDragAndDropEnabled) && (
            <div className="text-sm text-gray-500 mt-2">
              {L.questions.attachment.select_or_drag_to_upload}
            </div>
          )}
        </div>
      )}

      {(props.Viewer === 'screener' || (props.viewInfo?.protectedSearch === 'true')) && !directUploadOnly && (limitOne ? expectMore : true) && <div>
        <div className="mt-4" title={L.questions.attachment.via_text}>
          {sent ? (
            <div>
              <b className="mr-2">{L.questions.attachment.message_sent}</b><SpacedSpinner />
            </div>
          ) : (
            <div>
              <p className="mb-2 text-sm text-gray-600">
                {L.questions.attachment.send_attachment_text}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={L.questions.attachment.phone_placeholder}
                  className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={() => send(phone)}
                  className="inline-flex justify-center w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {L.questions.attachment.send}
                </button>
              </div>
            </div>
          )}
        </div>
        {allowSendingLinkToUpload && (
          <div className="mt-4 mb-4">
            <p className="mb-2 text-sm text-gray-600">
              {L.questions.attachment.send_link_to_upload}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                className="inline-flex justify-center w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={async () => {
                  setSendingSMSLink(true);
                  await sendLinkToGeneratedSuburvey('sms');
                  setSendingSMSLink(false);
                }}
              >
                {sendingSMSLink ? <SpacedSpinner /> : L.questions.attachment.via_text}
              </button>
              <button
                className="inline-flex justify-center w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={async () => {
                  setSendingEmailLink(true);
                  await sendLinkToGeneratedSuburvey('email');
                  setSendingEmailLink(false);
                }}
              >
                {sendingEmailLink ? <SpacedSpinner /> : L.questions.attachment.via_email}
              </button>
            </div>
          </div>
        )}
        <FromComms
          uid={props.uid}
          attachments={attachments}
          setSelected={setSelectedMediaFromMessage}
          setSelectedMetadata={setSelectedMetadataFromMessage}
          setUrlMetadataLookup={(rec) => attachmentDispatch(rec)} />
      </div>}
    </div>
  }

  return (
    <div>
      {marked}
      {renderedAttachments}
      <br />
      {(limitOne ? expectMore : true) && <div>
        <Tabs defaultActiveKey={subsurvey || directUploadOnly ? "direct" : "text"}>
          {!subsurvey && !directUploadOnly && (
            <Tab eventKey="text" title={L.questions.attachment.via_text}>
              <br />
              {sent && <b>{L.questions.attachment.message_sent}</b>}
              {!sent && (
                <InputGroup>
                  <Form.Control
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={L.questions.attachment.phone_placeholder}
                  />
                  <InputGroup.Append>
                    <Button
                      onClick={(e) => {
                        send(phone);
                      }}
                    >
                      {L.questions.attachment.send_attachment_text}
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              )}
            </Tab>
          )}
          <Tab eventKey="direct" title={L.questions.attachment.via_direct_upload}>
            <br />
            {uploadForm}
          </Tab>
          {enableCamera && (
            <Tab
              onEnter={() => setCameraActive(true)}
              onExit={() => setCameraActive(false)}
              title={L.selfie.camera}
              eventKey="from-camera">
              <CameraAttachment
                expectMore={true}
                enableCamera={enableCamera}
                onStateChange={(s) => setCameraActive(s === 'enabled')}
                doUpload={doUpload}
                enabled={cameraActive}
              />
            </Tab>
          )}
          {!directUploadOnly && props.uid && (
            <Tab eventKey="from-comms" title="...">
              <FromComms uid={props.uid} attachments={attachments}
                setSelected={setSelectedMediaFromMessage} />

            </Tab>
          )}
        </Tabs>
      </div>}
    </div>
  );
}

